/**
 * Promo Countdown Bar
 * Handles countdown timer and server time calibration.
 *
 * TIMEZONE BEHAVIOR:
 * Dates are interpreted relative to a configurable Mexican timezone.
 * All visitors see the same countdown regardless of their local timezone.
 *
 * DEBUG: Open console and run:
 *   PromoCountdownBar.debug();
 */
(function () {
  'use strict';

  const CLOCK_DRIFT_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes (Shopify CDN can cache pages for a while)

  /**
   * Initialize all promo countdown bars on the page.
   */
  function init() {
    const bars = document.querySelectorAll('.promo-countdown-bar');
    bars.forEach(initBar);
  }

  /**
   * Build a UTC timestamp from date components and a timezone offset.
   * @param {number} year
   * @param {number} month - 1-12
   * @param {number} day
   * @param {number} hour
   * @param {number} minute
   * @param {number} offsetMinutes - timezone offset from UTC (negative for Americas)
   * @returns {number} UTC timestamp in milliseconds
   */
  function buildUtcTimestamp(year, month, day, hour, minute, offsetMinutes) {
    // Date.UTC(year, month, day, hour, minute) treats the input as UTC+0
    // But our date/time is in a LOCAL timezone (e.g. 23:59 in UTC-6)
    // To convert local time to UTC, we SUBTRACT the offset:
    // Example: 23:59 in UTC-6 → 23:59 UTC + 6 hours = 05:59 UTC next day
    const utcBase = Date.UTC(year, month - 1, day, hour, minute, 0);
    return utcBase - (offsetMinutes * 60 * 1000);
  }

  /**
   * Initialize a single promo countdown bar.
   * @param {HTMLElement} bar
   */
  function initBar(bar) {
    const sectionId = bar.dataset.sectionId;
    const serverNowSeconds = parseInt(bar.dataset.serverNow || '0', 10);
    const timezoneOffset = parseInt(bar.dataset.timezoneOffset || '-360', 10);

    // Parse start date components
    const startYear = parseInt(bar.dataset.startYear, 10);
    const startMonth = parseInt(bar.dataset.startMonth, 10);
    const startDay = parseInt(bar.dataset.startDay, 10);
    const startHour = parseInt(bar.dataset.startHour, 10);
    const startMinute = parseInt(bar.dataset.startMinute, 10);

    // Parse end date components
    const endYear = parseInt(bar.dataset.endYear, 10);
    const endMonth = parseInt(bar.dataset.endMonth, 10);
    const endDay = parseInt(bar.dataset.endDay, 10);
    const endHour = parseInt(bar.dataset.endHour, 10);
    const endMinute = parseInt(bar.dataset.endMinute, 10);

    if (!sectionId ||
        isNaN(startYear) || isNaN(startMonth) || isNaN(startDay) ||
        isNaN(endYear) || isNaN(endMonth) || isNaN(endDay)) {
      hideBar(bar);
      return;
    }

    // Build UTC timestamps using the Mexican reference timezone
    const startTimestamp = buildUtcTimestamp(startYear, startMonth, startDay, startHour, startMinute, timezoneOffset);
    const endTimestamp = buildUtcTimestamp(endYear, endMonth, endDay, endHour, endMinute, timezoneOffset);
    const startDate = new Date(startTimestamp);
    const endDate = new Date(endTimestamp);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      hideBar(bar);
      return;
    }

    // Server time calibration
    // NOTE: The server timestamp (data-server-now) can be stale due to Shopify CDN caching.
    // If the drift is too large, we fall back to the client's clock instead of hiding the bar.
    const clientNow = Date.now();
    const serverNow = serverNowSeconds * 1000;
    let clockDrift = 0;

    if (serverNow > 0) {
      const rawDrift = clientNow - serverNow;
      // Only apply server calibration if drift is within a reasonable window
      if (Math.abs(rawDrift) <= CLOCK_DRIFT_THRESHOLD_MS) {
        clockDrift = rawDrift;
      }
      // If drift exceeds the threshold, clockDrift stays 0 (use client time as-is)
    }

    const now = new Date(Date.now() - clockDrift);

    // Check if promo is active
    if (now < startDate || now >= endDate) {
      hideBar(bar);
      return;
    }

    // Show the bar
    showBar(bar);

    // Update height CSS variable with ResizeObserver
    const resizeObserver = new ResizeObserver(() => updateBarHeight(bar));
    resizeObserver.observe(bar);

    // Setup timer
    const daysEl = bar.querySelector('[data-timer="days"]');
    const hoursEl = bar.querySelector('[data-timer="hours"]');
    const minutesEl = bar.querySelector('[data-timer="minutes"]');

    if (!daysEl || !hoursEl || !minutesEl) {
      return;
    }

    function updateTimer() {
      const currentNow = new Date(Date.now() - clockDrift);
      const diff = endDate.getTime() - currentNow.getTime();

      if (diff <= 0) {
        hideBar(bar);
        resizeObserver.disconnect();
        updateBarHeight(null);
        return;
      }

      const totalMinutes = Math.floor(diff / (1000 * 60));
      const totalHours = Math.floor(totalMinutes / 60);
      const days = Math.floor(totalHours / 24);
      const hours = totalHours % 24;
      const minutes = totalMinutes % 60;

      daysEl.textContent = String(days).padStart(2, '0');
      hoursEl.textContent = String(hours).padStart(2, '0');
      minutesEl.textContent = String(minutes).padStart(2, '0');
    }

    updateTimer();
    setInterval(updateTimer, 1000 * 60); // Update every minute
  }

  /**
   * Update the --promo-bar-height CSS variable on :root
   * @param {HTMLElement | null} bar
   */
  function updateBarHeight(bar) {
    if (bar && !bar.classList.contains('is-hidden')) {
      const height = bar.offsetHeight;
      document.documentElement.style.setProperty('--promo-bar-height', `${height}px`);
    } else {
      document.documentElement.style.setProperty('--promo-bar-height', '0px');
    }
  }

  /**
   * Hide the bar.
   * @param {HTMLElement} bar
   */
  function hideBar(bar) {
    bar.classList.add('is-hidden');
  }

  /**
   * Show the bar.
   * @param {HTMLElement} bar
   */
  function showBar(bar) {
    bar.classList.remove('is-hidden');
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Handle Shopify Theme Editor events
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.classList.contains('promo-countdown-bar-section')) {
      const bar = event.target.querySelector('.promo-countdown-bar');
      if (bar) initBar(bar);
    }
  });

  document.addEventListener('shopify:section:select', function (event) {
    if (event.target.classList.contains('promo-countdown-bar-section')) {
      const bar = event.target.querySelector('.promo-countdown-bar');
      if (bar) {
        showBar(bar);
        updateBarHeight(bar);
      }
    }
  });

  // Expose debug utilities
  window.PromoCountdownBar = {
    /**
     * Show debug info for all promo bars
     */
    debug() {
      const bars = document.querySelectorAll('.promo-countdown-bar');
      bars.forEach((bar) => {
        const tzOffset = parseInt(bar.dataset.timezoneOffset || '-360', 10);
        const tzName = tzOffset === -360 ? 'Centro (UTC-6)' :
                       tzOffset === -420 ? 'Noroeste (UTC-7)' :
                       tzOffset === -480 ? 'Pacífico (UTC-8)' :
                       tzOffset === -300 ? 'Sureste (UTC-5)' : 'Unknown';

        const startTimestamp = buildUtcTimestamp(
          parseInt(bar.dataset.startYear, 10),
          parseInt(bar.dataset.startMonth, 10),
          parseInt(bar.dataset.startDay, 10),
          parseInt(bar.dataset.startHour, 10),
          parseInt(bar.dataset.startMinute, 10),
          tzOffset
        );
        const endTimestamp = buildUtcTimestamp(
          parseInt(bar.dataset.endYear, 10),
          parseInt(bar.dataset.endMonth, 10),
          parseInt(bar.dataset.endDay, 10),
          parseInt(bar.dataset.endHour, 10),
          parseInt(bar.dataset.endMinute, 10),
          tzOffset
        );

        const startDate = new Date(startTimestamp);
        const endDate = new Date(endTimestamp);
        const now = new Date();
        const diff = endTimestamp - now.getTime();

        console.group('Promo Countdown Bar Debug');
        console.log('Section ID:', bar.dataset.sectionId);
        console.log('Reference timezone:', tzName);
        console.log('User browser timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);
        console.log('User local time:', now.toString());
        console.log('Promo start (UTC):', startDate.toISOString());
        console.log('Promo end (UTC):', endDate.toISOString());
        console.log('Is active:', now >= startDate && now < endDate);
        console.log('Time remaining (ms):', diff);
        console.log('Bar hidden:', bar.classList.contains('is-hidden'));
        console.groupEnd();
      });
    },

    /**
     * Show supported Mexican timezones
     */
    timezones() {
      console.log('Supported Mexican reference timezones:');
      console.log('  Centro (UTC-6): CDMX, Guadalajara, Monterrey, etc.');
      console.log('  Noroeste (UTC-7): BCS, Sonora, Chihuahua, Sinaloa, Nayarit');
      console.log('  Pacífico (UTC-8): Baja California');
      console.log('  Sureste (UTC-5): Quintana Roo');
    }
  };
})();
